<script lang="ts">
  import type { MaturityScore, MaturityBand } from '$core/score/score.types';
  import { formatScore } from '$core/format/numberFormat';
  import Card from '../../components/Card.svelte';

  export let score: MaturityScore;

  const bandLabel: Record<MaturityBand, string> = {
    critico: 'Crítico',
    atencao: 'Atenção',
    consistente: 'Consistente',
    exemplar: 'Exemplar',
  };

  function bandClass(band: MaturityBand): string {
    return `badge--${band}`;
  }
</script>

<section class="maturity-score-card" aria-label="Score de maturidade">
  <Card elevated padding="lg">
    <div class="maturity-score-card__header">
      <div class="maturity-score-card__total" aria-label="Score total {formatScore(score.total)} de 100">
        <span class="maturity-score-card__number">{formatScore(score.total)}</span>
        <span class="maturity-score-card__denom">/100</span>
      </div>
      <div class="maturity-score-card__meta">
        <span class="badge {bandClass(score.band)}" aria-label="Banda: {bandLabel[score.band]}">
          {bandLabel[score.band]}
        </span>
        {#if score.pilot}
          <p class="maturity-score-card__pilot-notice">{score.pilotNotice}</p>
        {/if}
      </div>
    </div>

    <div class="maturity-score-card__dimensions" aria-label="Dimensões do score">
      {#each score.dimensions as dim (dim.code)}
        <div class="dimension">
          <div class="dimension__header">
            <span class="dimension__label">{dim.label}</span>
            <span class="dimension__value" class:dimension__value--underdetermined={dim.underdetermined}>
              {dim.underdetermined ? '—' : formatScore(dim.value)}
            </span>
          </div>
          <div class="dimension__bar-track" role="progressbar" aria-valuenow={dim.underdetermined ? 0 : dim.value} aria-valuemin={0} aria-valuemax={100}>
            <div
              class="dimension__bar-fill"
              style="width: {dim.underdetermined ? 0 : dim.value}%"
            />
          </div>
          {#if dim.rationale}
            <p class="dimension__rationale">{dim.rationale}</p>
          {/if}
        </div>
      {/each}
    </div>
  </Card>
</section>

<style>
  .maturity-score-card__header {
    display: flex;
    align-items: flex-start;
    gap: var(--ptt-space-5);
    margin-bottom: var(--ptt-space-6);
    flex-wrap: wrap;
  }

  .maturity-score-card__total {
    display: flex;
    align-items: baseline;
    gap: var(--ptt-space-1);
  }

  .maturity-score-card__number {
    font-size: 4rem;
    font-weight: 700;
    line-height: 1;
    color: var(--ptt-gray-300);
  }

  .maturity-score-card__denom {
    font-size: 1.5rem;
    font-weight: 400;
    color: var(--ptt-gray-100);
  }

  .maturity-score-card__meta {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-2);
    padding-top: var(--ptt-space-2);
  }

  .maturity-score-card__pilot-notice {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin: 0;
    max-width: 320px;
  }

  /* Badges */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: var(--ptt-space-1) var(--ptt-space-3);
    border-radius: var(--ptt-radius-sm);
    font-size: 0.875rem;
    font-weight: 600;
  }

  .badge--critico {
    background: var(--ptt-orange-100);
    color: var(--ptt-white);
  }

  .badge--atencao {
    background: var(--ptt-yellow-200);
    color: var(--ptt-black);
  }

  .badge--consistente {
    background: var(--ptt-blue-100);
    color: var(--ptt-white);
  }

  .badge--exemplar {
    background: var(--ptt-green-100);
    color: var(--ptt-white);
  }

  /* Dimensions */
  .maturity-score-card__dimensions {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  .dimension {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .dimension__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .dimension__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-200);
  }

  .dimension__value {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
  }

  .dimension__value--underdetermined {
    color: var(--ptt-gray-100);
  }

  .dimension__bar-track {
    height: 6px;
    background: var(--ptt-gray-20);
    border-radius: var(--ptt-radius-sm);
    overflow: hidden;
  }

  .dimension__bar-fill {
    height: 100%;
    background: var(--ptt-purple-100);
    border-radius: var(--ptt-radius-sm);
    transition: width 0.3s ease;
  }

  .dimension__rationale {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin: 0;
  }

  @media (max-width: 480px) {
    .maturity-score-card__number {
      font-size: 3rem;
    }
  }
</style>
